<template>
  <div :id="chartData._id" class="jzliu-bi-engine-chart-warp" :style="{ height: warpHeight }"></div>
</template>

<script>
import { Gauge } from '@antv/g2plot'
import { getCategoryDimension, initHeight } from '../../utils/index'
import { customKey } from '../../utils/enum.data'
import { hexToRgba } from '../../utils/index'
import { styleConfig } from '../../utils/panelConf'

export default {
  name: 'cGauge',
  props: {
    chartData: {
      type: Object,
      default: () => {
        return {}
      },
    },
    chartConfigHandler: {
      type: Function,
      default: (conf) => conf,
    },
  },
  data() {
    return {
      warpHeight: 200,
      renderTimer: null,
      gaugePlot: null,
    }
  },
  watch: {
    'chartData.styleConfig.helperData.fieldMap': {
      handler() {
        this.debounceRenderChart()
      },
      deep: true
    },
    'chartData.styleConfig.commongConfig': {
      handler() {
        this.debounceRenderChart()
      },
      deep: true
    }
  },
  mounted() {
    this.$nextTick(() => {
      initHeight.call(this, 'warpHeight')
      this.renderChart()
    })
  },
  methods: {
    // 防抖渲染图表
    debounceRenderChart() {
      if (this.renderTimer) {
        clearTimeout(this.renderTimer)
      }
      this.renderTimer = setTimeout(() => {
        this.gaugePlot && this.gaugePlot.destroy()
        this.renderChart()
      }, 300) // 300ms 防抖延迟
    },

    // 格式化数值：超过10000自动转换为万，保留三位小数
    formatNumber(value) {
      const num = Number(value)
      if (isNaN(num)) {
        return value
      }

      // 如果数值大于等于10000，转换为万
      if (Math.abs(num) >= 10000) {
        const wanValue = num / 10000
        return wanValue.toFixed(3) + '万'
      }

      // 如果数值小于10000，保留三位小数
      return num.toFixed(3)
    },

    aggregateDataByDimension(data, dimensionField, measureField) {
      if (!data || !Array.isArray(data) || !dimensionField || !measureField) {
        return data || []
      }

      // 使用 Map 来聚合相同维度值的数据
      const aggregatedMap = new Map()

      data.forEach(item => {
        const dimensionValue = item[dimensionField]
        const measureValue = parseFloat(item[measureField]) || 0

        if (aggregatedMap.has(dimensionValue)) {
          // 如果已存在该维度值，累加度量值
          const existingItem = aggregatedMap.get(dimensionValue)
          existingItem[measureField] += measureValue

          // 保留其他字段的最新值（可选，根据业务需求调整）
          Object.keys(item).forEach(key => {
            if (key !== dimensionField && key !== measureField) {
              existingItem[key] = item[key]
            }
          })
        } else {
          // 如果不存在该维度值，创建新记录
          const newItem = { ...item }
          newItem[measureField] = measureValue
          aggregatedMap.set(dimensionValue, newItem)
        }
      })

      // 将 Map 转换为数组
      return Array.from(aggregatedMap.values())
    },
    renderChart() {
      const vm = this
      // 饼图不存在行列的概念 所以维度只存在于rows中 有且只有1个维度
      // 色块区域取度量第一个 其他以tooltip展示
      const {
        _id: id,
        data: {
          data: chartData,
          meta,
        },
      } = this.chartData
      const commongConfig = this.chartData.styleConfig.commongConfig ?? styleConfig().commongConfig
      const labelConfig = commongConfig.labelConfig
      const { rows, values, columns } = getCategoryDimension(this.chartData.fields)
      const key = columns[0]
      const nameMap = this.chartData.styleConfig.helperData.nameMap
      const opt = {
        total:chartData[0]?.total_value ?? 0,
        value:chartData[0]?.[key] ?? 0,
      }
      const total = opt.total
      const conf = {
        percent: opt.value / opt.total,
        radius: 1,
        innerRadius: commongConfig.gaugeRadiusRatio / 100,
        range: {
          color: `l(0) 0:${hexToRgba(commongConfig.gaugeThemeColor, commongConfig.gaugeThemeOpacity / 100)} 1:${commongConfig.gaugeThemeColor}`,
        },
        axis: {
          label: commongConfig.gaugeInnerTickEnable ? {
            formatter: (v) => {
              if (commongConfig.gaugeInnerTick === 1) {
                return this.formatNumber(Number(v) * total);
              }
              return Number(v) * 100 + '%';
            },
          } : null,
          tickLine: commongConfig.gaugeInnerTickEnable ? {
            count: 3,
          } : null,
          subTickLine: commongConfig.gaugeInnerTickEnable ? {
            count: 3,
          } : null,
          tickLabel: commongConfig.gaugeInnerTickEnable ? {
            formatter: (v) => {
              if (commongConfig.gaugeInnerTick === 1) {
                return this.formatNumber(Number(v) * total);
              }
              return Number(v) * 100 + '%';
            },
          } : null,
        },
        indicator: {
          pointer: {
            style: {
              stroke: '#D0D0D0',
            },
          },
          pin: {
            style: {
              stroke: '#D0D0D0',
            },
          },
        },
        statistic: {
          title: {
            offsetY: -40,
            customHtml: () => `
              <div style="text-align: center;line-height: 1;">
                 <div style="font-size:${labelConfig.textConfig.labelFontSize}px;color:${labelConfig.textConfig.labelColor};font-weight:${labelConfig.textConfig.labelIsBold ? 'bold' : 'normal'};font-style:${labelConfig.textConfig.labelIsItalic ? 'italic' : 'normal'}">${nameMap[key]}</div>
                 <div style="font-size:${labelConfig.textConfig.valueFontSize}px;color:${labelConfig.textConfig.valueColor};font-weight:${labelConfig.textConfig.valueIsBold ? 'bold' : 'normal'};font-style:${labelConfig.textConfig.valueIsItalic ? 'italic' : 'normal'};margin-top:6px">${commongConfig.gaugeLabelStyle === 1 ? vm.formatNumber(opt.value) : Number(opt.value / opt.total * 100).toFixed(labelConfig.textConfig.labelDecimalPlaces) + '%'}</div>
              </div>`,
          },
          content: commongConfig.labelConfig.textConfig.isShow ? {
            offsetY: 30,
            customHtml: () => `
              <div style="text-align: center;">
                 <div style="font-size:${commongConfig.labelConfig.textConfig.fontSize}px;color:${commongConfig.labelConfig.textConfig.color};font-weight:${commongConfig.labelConfig.textConfig.isBold ? 'bold' : 'normal'};font-style:${commongConfig.labelConfig.textConfig.isItalic ? 'italic' : 'normal'}">${commongConfig.gaugeLabelStyle === 0 ? '实际:' : '占比'} ${commongConfig.gaugeLabelStyle === 0 ? vm.formatNumber(opt.value) : Number(opt.value / opt.total * 100).toFixed(labelConfig.textConfig.labelDecimalPlaces) + '%'}</div>
              </div>`,
          } : null,
        },
      }
      const confOpt = this.chartData.isCancel ? conf : this.chartConfigHandler(conf, this.chartData)
      this.gaugePlot = new Gauge(id, {
        ...confOpt,
      })
      this.gaugePlot.render()
    },
    destroyedChart() {
      this.gaugePlot?.destroy()
    }
  },
}
</script>

<style scoped lang="less"></style>
